import { useState } from 'react';
import { AlertTriangle, Check, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { SheetFooter } from '@/components/ui/sheet';
import type { HelpScoutConnectionStatus } from '@/services/integration.service';

interface AuthorizationActionProps {
	isAuthorizing: boolean;
	onAuthorize: () => void;
}

export const HelpScoutSuccessScreen = ({ connection, onDone }: { connection: HelpScoutConnectionStatus; onDone: () => void }) => (
	<div className='flex h-full flex-col'>
		<div className='flex flex-1 flex-col items-center px-5 pt-6 text-center'>
			<div className='grid size-13 place-items-center rounded-full bg-emerald-50 text-emerald-700'><Check className='size-7' strokeWidth={2} /></div>
			<h3 className='mt-2 text-sm font-semibold'>Help Scout connected</h3>
			<p className='mt-1 text-xs text-muted-foreground'>Webhook configured automatically · All accessible inboxes connected</p>
			{connection.replyIdentityMode === 'dedicated_user' && <p className='mt-1 text-xs text-muted-foreground'>Dedicated Kim AI reply identity created automatically</p>}
		</div>
		<SheetFooter className='h-[76px] flex-row items-center justify-end border-t px-5 py-4'>
			<Button type='button' size='sm' className='h-[34px]' onClick={onDone}>Done</Button>
		</SheetFooter>
	</div>
);

const getAttentionMessage = (connection: HelpScoutConnectionStatus): string | undefined => {
	if (connection.replyIdentityMode === 'oauth_owner_fallback' && connection.attentionCode === 'dedicated_user_unavailable') {
		return 'Replies use the authorized Help Scout owner because a dedicated Kim AI identity is unavailable. Reconnect to retry setup.';
	}
	if (connection.replyIdentityMode === 'unknown') {
		return 'Reply identity setup needs attention. Reconnect Help Scout to verify the reply identity.';
	}
	if (connection.attentionCode === 'webhook_validation_failed') {
		return 'Automatic webhook validation did not complete. Reconnect Help Scout to retry setup.';
	}
	return undefined;
};

interface ManagementScreenProps extends AuthorizationActionProps {
	connection: HelpScoutConnectionStatus;
	disconnectError?: string;
	isDisconnecting: boolean;
	onDisconnect: () => void;
}

export const HelpScoutManagementScreen = ({
	connection,
	disconnectError,
	isAuthorizing,
	isDisconnecting,
	onAuthorize,
	onDisconnect
}: ManagementScreenProps) => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const webhookHealthy = connection.webhookConfigured;
	const needsAttention = Boolean(connection.needsAttention || !webhookHealthy);
	const attentionMessage = getAttentionMessage(connection);

	return (
		<div className='flex h-full flex-col'>
			<div className='flex flex-1 flex-col px-5'>
				<div className='flex items-start justify-between gap-3'>
					<div>
						<h3 className='text-sm font-semibold'>Help Scout connected</h3>
						{connection.companyName && <p className='mt-0.5 text-xs text-muted-foreground'>{connection.companyName}</p>}
					</div>
					<span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${needsAttention ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
						<span className={`size-2 rounded-full ${needsAttention ? 'bg-amber-500' : 'bg-green-500'}`} /> {needsAttention ? 'Needs attention' : 'Connected'}
					</span>
				</div>

				<div className='mt-5 flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-xs'>
					<div>
						<p className='font-medium'>Connection</p>
						<p className='mt-0.5 text-[11px] text-muted-foreground'>
							{webhookHealthy ? 'Webhook configured automatically' : 'Webhook setup needs attention'} · All accessible inboxes connected{connection.inboxCount ? ` (${connection.inboxCount})` : ''}
						</p>
					</div>
					<div className='flex items-center gap-1'>
						<span className={`rounded-full px-2 py-1 text-[11px] font-medium ${webhookHealthy ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{webhookHealthy ? 'Healthy' : 'Needs attention'}</span>
						<Button type='button' variant='ghost' size='icon-sm' aria-label='Reconnect Help Scout' title='Reconnect Help Scout' onClick={onAuthorize} disabled={isAuthorizing}><RefreshCw className='size-3.5' /></Button>
					</div>
				</div>
				{connection.replyIdentityMode === 'dedicated_user' && <p className='mt-3 text-[11px] text-muted-foreground'>Dedicated Kim AI reply identity created automatically.</p>}
				{attentionMessage && <div className='mt-3 flex gap-2 text-xs text-amber-700'><AlertTriangle className='mt-0.5 size-3.5 shrink-0' /><span>{attentionMessage}</span></div>}
				<div className='my-5 border-t' />
				<div className='rounded-lg border border-destructive/30 p-3 text-xs'>
					<p className='font-semibold'>Disconnect Help Scout</p>
					<p className='mt-1 text-[11px] text-muted-foreground'>Stop Help Scout events and Kim replies.</p>
					<AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
						<AlertDialogTrigger asChild><Button type='button' variant='outline' size='sm' className='mt-3 border-destructive text-destructive hover:bg-red-50 hover:text-destructive'>Disconnect</Button></AlertDialogTrigger>
						<AlertDialogContent overlayClassName='bg-black/[0.24]' className='max-w-[420px] gap-0 rounded-xl p-5 sm:max-w-[420px]'>
							<AlertDialogHeader className='gap-0'>
								<AlertDialogTitle className='text-sm'>Disconnect Help Scout?</AlertDialogTitle>
								<AlertDialogDescription className='mt-1 text-xs leading-[18px]'>Kim will stop receiving Help Scout conversations and remove its managed webhook. This won’t delete data in Help Scout.</AlertDialogDescription>
							</AlertDialogHeader>
							{disconnectError && <div role='alert' className='mt-3 rounded-md border border-destructive/30 bg-red-50 px-3 py-2 text-xs text-destructive'>{disconnectError}</div>}
							<AlertDialogFooter className='mt-[18px]'>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									className='border border-destructive bg-white text-destructive hover:bg-red-50'
									disabled={isDisconnecting}
									onClick={(event) => {
										event.preventDefault();
										onDisconnect();
									}}
								>
									{disconnectError ? 'Try again' : 'Disconnect Help Scout'}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>
		</div>
	);
};

export const HelpScoutConnectingScreen = () => (
	<div className='flex h-full flex-col'>
		<div className='flex flex-1 flex-col items-center px-5 pt-6 text-center' role='status'>
			<Loader2 className='mb-5 size-[26px] animate-spin text-muted-foreground' />
			<h3 className='text-sm font-semibold'>Connecting Help Scout…</h3>
			<p className='mt-1 text-xs text-muted-foreground'>Kim is finishing the connection automatically.</p>
		</div>
		<SheetFooter className='h-[76px] flex-row items-center justify-end border-t px-5 py-4'><Button size='sm' className='h-[34px]' disabled><Loader2 className='size-4 animate-spin' /> Connecting…</Button></SheetFooter>
	</div>
);

interface AuthorizationScreenProps extends AuthorizationActionProps {
	status: HelpScoutConnectionStatus['status'];
	errorMessage?: string;
}

export const HelpScoutAuthorizationScreen = ({ status, errorMessage, isAuthorizing, onAuthorize }: AuthorizationScreenProps) => {
	const needsAttention = status === 'needs_attention';
	return (
		<div className='flex h-full flex-col'>
			<div className='flex flex-1 flex-col gap-4 px-5'>
				<div>
					<h3 className='text-sm font-semibold'>{needsAttention ? 'Help Scout needs attention' : errorMessage ? 'Couldn’t connect Help Scout' : 'Connect Help Scout'}</h3>
					<p className='mt-1 text-xs text-muted-foreground'>{errorMessage ? 'Kim couldn’t finish the connection. Authorize again to continue.' : 'Authorize your account so Kim can connect Help Scout automatically.'}</p>
				</div>
				{errorMessage && <div role='alert' className='flex max-w-[470px] gap-2 rounded-[7px] border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-[17px] text-red-700'><AlertTriangle className='mt-0.5 size-4 shrink-0' /><span>{errorMessage}</span></div>}
			</div>
			<SheetFooter className={`h-[76px] flex-row items-center border-t px-5 py-4 ${errorMessage ? 'justify-start' : 'justify-end'}`}>
				<Button type='button' size='sm' className='h-[34px]' onClick={onAuthorize} disabled={isAuthorizing}>
					<ExternalLink className='size-4' /> {errorMessage ? 'Authorize again' : 'Authorize with Help Scout'}
				</Button>
			</SheetFooter>
		</div>
	);
};
